import { useCallback, useMemo, useState } from 'react';

import type { QuickSizeFilterOption, UnitFilterRule } from '@r6digital/rapidstor-types/react/entities/Journey';

import {
    SpaceCalculatorResult,
    spaceCalculatorFilterRules,
} from './components/space-calculator/calculator';
import { ALL_QUICK_SIZE_KEY, VEHICLE_QUICK_SIZE_KEY } from './filters';

type ListingSizeFilter =
    | { source: 'quickSize'; key: string }
    | { source: 'spaceCalculator'; result: SpaceCalculatorResult };

interface ResolvedSizeFilter {
    activeQuickSizeKey: string;
    rules: UnitFilterRule[];
}

/**
 * The size filter narrowing the listing: either a quick size button or the
 * last space calculator result, whichever the customer used most recently.
 */
export default function useListingSizeFilter(quickSizeOptions: QuickSizeFilterOption[]) {
    const [sizeFilter, setSizeFilter] = useState<ListingSizeFilter>({
        source: 'quickSize',
        key: ALL_QUICK_SIZE_KEY,
    });

    const { activeQuickSizeKey, rules } = useMemo((): ResolvedSizeFilter => {
        if (sizeFilter.source === 'spaceCalculator') {
            const highlightsVehicle = sizeFilter.result.vehicle
                && quickSizeOptions.some((option) => option.key === VEHICLE_QUICK_SIZE_KEY);
            return {
                activeQuickSizeKey: highlightsVehicle ? VEHICLE_QUICK_SIZE_KEY : ALL_QUICK_SIZE_KEY,
                rules: spaceCalculatorFilterRules(sizeFilter.result),
            };
        }
        // A config change can remove the selected button.
        const selected = quickSizeOptions.find((option) => option.key === sizeFilter.key);
        return {
            activeQuickSizeKey: selected?.key ?? ALL_QUICK_SIZE_KEY,
            rules: selected?.filters ?? [],
        };
    }, [sizeFilter, quickSizeOptions]);

    const selectQuickSize = useCallback((key: string) => {
        setSizeFilter({ source: 'quickSize', key });
    }, []);

    const applySpaceCalculatorResult = useCallback((result: SpaceCalculatorResult) => {
        setSizeFilter({ source: 'spaceCalculator', result });
    }, []);

    return {
        activeQuickSizeKey,
        rules,
        selectQuickSize,
        applySpaceCalculatorResult,
    };
}
